import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  User,
  Phone,
  Mail,
  Lock,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { API_URL } from "./Config/Env";
import Swal from "sweetalert2";

export default function Signup() {
  const navigate = useNavigate();

  const [isSigningUp, setIsSigningUp] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phonenumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Name Required",
        text: "Please enter your full name.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    // Kenyan phone validation
    if (!form.phonenumber.match(/^(\+254|0)[17]\d{8}$/)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone Number",
        text: "Please enter a valid Kenyan phone number, for example 0712345678.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (form.password.length < 8) {
      Swal.fire({
        icon: "warning",
        title: "Password Too Short",
        text: "Your password must contain at least 8 characters.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (form.password !== form.confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Passwords Do Not Match",
        text: "Please make sure both passwords are the same.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    const formData = new FormData();

    formData.append("name", form.name.trim());
    formData.append("phonenumber", form.phonenumber.trim());
    formData.append("email", form.email.trim());
    formData.append("password", form.password);

    setIsSigningUp(true);

    try {
      const response = await fetch(`${API_URL}/signup/`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: "Signup Failed",
          text:
            data.message ||
            data.detail ||
            "We couldn't create your account.",
          confirmButtonColor: "#2563EB",
        });

        return;
      }

      await Swal.fire({
        icon: "success",
        title: "Account Created",
        text: "Your Nexindi account has been created successfully. You can now sign in.",
        confirmButtonColor: "#2563EB",
      });

      setForm({
        name: "",
        phonenumber: "",
        email: "",
        password: "",
        confirmPassword: "",
      });

      navigate("/signin");
    } catch (error) {
      console.error("Signup error:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to Create Account",
        text: "Something went wrong. Please check your connection and try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setIsSigningUp(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="min-h-screen grid lg:grid-cols-2">
        {/* Left Brand Section */}
        <section className="relative hidden lg:flex bg-slate-950 text-white overflow-hidden">
          {/* Background Glow */}
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-600/25 rounded-full blur-[150px]" />

          <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

          {/* Grid */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
              `,
              backgroundSize: "50px 50px",
            }}
          />

          <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
            {/* Logo */}
            <Link
              to="/"
              className="inline-flex items-center gap-3 w-fit"
            >
              <img
                src="/logo.png"
                alt="Nexindi Tech"
                className="w-12 h-12 rounded-xl object-cover border border-white/20"
              />

              <div>
                <p className="font-bold text-xl tracking-tight">
                  NEXINDI
                </p>

                <p className="text-xs tracking-[0.25em] uppercase text-blue-400">
                  Tech
                </p>
              </div>
            </Link>

            {/* Brand Content */}
            <div className="max-w-xl py-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
                <span className="w-2 h-2 bg-blue-400 rounded-full" />

                Join Nexindi
              </div>

              <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-[1.08] mb-7">
                Create your account and
                <span className="block text-blue-400">
                  build with technology.
                </span>
              </h1>

              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-lg">
                Create your Nexindi account to access services, place
                requests, manage orders, interact with our products,
                and stay connected with our technology ecosystem.
              </p>

              <div className="space-y-4">
                {[
                  "Request technology services",
                  "Manage your orders and activity",
                  "Access Nexindi digital platforms",
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 text-slate-300"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-400/20 flex items-center justify-center">
                      <CheckCircle2
                        size={16}
                        className="text-blue-400"
                      />
                    </div>

                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-sm text-slate-500">
              Empowering Africa through technology.
            </p>
          </div>
        </section>

        {/* Right Signup Section */}
        <section className="relative flex items-center justify-center px-6 py-12 md:px-12 bg-white">
          {/* Mobile Background Glow */}
          <div className="lg:hidden absolute top-0 left-0 w-[300px] h-[300px] bg-blue-100 rounded-full blur-[120px] opacity-60" />

          <div className="relative z-10 w-full max-w-md">
            {/* Mobile Logo */}
            <Link
              to="/"
              className="lg:hidden flex items-center justify-center gap-3 mb-10"
            >
              <img
                src="/logo.png"
                alt="Nexindi Tech"
                className="w-12 h-12 rounded-xl object-cover"
              />

              <div>
                <p className="font-bold text-xl text-slate-900 leading-tight">
                  NEXINDI
                </p>

                <p className="text-[10px] text-blue-600 uppercase tracking-[0.22em]">
                  Tech
                </p>
              </div>
            </Link>

            {/* Form Header */}
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold mb-5">
                <ShieldCheck size={14} />
                Create Account
              </div>

              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-3">
                Get started
              </h2>

              <p className="text-slate-500 leading-relaxed">
                Create your Nexindi Tech account in just a few
                steps.
              </p>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              encType="multipart/form-data"
            >
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Full Name
                </label>

                <div className="relative">
                  <User
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="name"
                    type="text"
                    name="name"
                    required
                    autoComplete="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phonenumber"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Phone Number
                </label>

                <div className="relative">
                  <Phone
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="phonenumber"
                    type="tel"
                    name="phonenumber"
                    required
                    autoComplete="tel"
                    value={form.phonenumber}
                    onChange={handleChange}
                    placeholder="0712345678"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Email Address
                </label>

                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="email"
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Password
                </label>

                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="password"
                    type={
                      showPassword ? "text" : "password"
                    }
                    name="password"
                    required
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Minimum 8 characters"
                    className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (prev) => !prev
                      )
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition"
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff size={19} />
                    ) : (
                      <Eye size={19} />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Confirm Password
                </label>

                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="confirmPassword"
                    type={
                      showConfirmPassword
                        ? "text"
                        : "password"
                    }
                    name="confirmPassword"
                    required
                    autoComplete="new-password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    placeholder="Repeat your password"
                    className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-slate-200 bg-white outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        (prev) => !prev
                      )
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition"
                    aria-label={
                      showConfirmPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={19} />
                    ) : (
                      <Eye size={19} />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSigningUp}
                className="group w-full mt-2 flex items-center justify-center gap-3 bg-blue-600 text-white py-3.5 px-6 rounded-xl font-semibold hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
              >
                {isSigningUp ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account

                    <ArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </form>

            {/* Sign In */}
            <div className="mt-7 pt-7 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-500">
                Already have an account?{" "}
                <Link
                  to="/signin"
                  className="font-semibold text-blue-600 hover:text-blue-700"
                >
                  Sign in
                </Link>
              </p>
            </div>

            {/* Security */}
            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <ShieldCheck
                size={19}
                className="text-blue-600 flex-shrink-0 mt-0.5"
              />

              <p className="text-xs text-slate-500 leading-relaxed">
                By creating an account, you agree to provide
                accurate information and keep your account
                credentials secure.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}